import React, { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import Box from '@tds/core-box';
import FlexGrid from '@tds/core-flex-grid';
import Input from '@tds/core-input';
import Select from '@tds/core-select';
import Button from '@tds/core-button';
import Notification from '@tds/core-notification';
import Strong from '@tds/core-strong';

import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';

const AddSubscriberForm = ({
  isCorporate,
  rateplanList,
  renewalSubscriberList,
  setRenewalSubscriberList
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: { value3year },
    iotOrderDetailsSection: {
      vehicleInfoMessage,
      vehicleVIN,
      vehicleMake,
      vehicleModel,
      vehicleYear,
      subscribersOfferList
    }
  } = localizedStrings;

  const [dataField, setDataFields] = useState({
    subscriber: '',
    plan: '',
    device: '',
    term: '',
    price: '',
    offer: '',
    vin: '',
    make: '',
    model: '',
    year: ''
  });
  const [error, setError] = useState('');
  const rateplanModel = useMemo(() => {
    return rateplanList.reduce((acc, current) => {
      if (!_.get(current, 'fields.isHeader', false)) {
        const isRoaming = _.get(
          current,
          'fields.show50PercentUsCheckBox',
          false
        );
        const deviceList = _.get(current, 'fields.deviceList', []).reduce(
          (deviceAcc, deviceCurrent) => {
            const termList = _.get(deviceCurrent, 'fields.terms', []).reduce(
              (termsAcc, termsCurrent) => {
                const { termPrice, termPrice50PrecentUs } = _.get(
                  termsCurrent,
                  'fields',
                  {}
                );
                return {
                  ...termsAcc,
                  [_.get(termsCurrent, 'fields.termName', '')]: {
                    termPrice,
                    termPrice50PrecentUs
                  }
                };
              },
              {}
            );
            return {
              ...deviceAcc,
              [_.get(deviceCurrent, 'fields.deviceName', '')]: { ...termList }
            };
          },
          {}
        );

        return {
          ...acc,
          [_.get(current, 'fields.rateplanNameRenewal', '')]: { ...deviceList },
          ...(isRoaming && {
            [`${_.get(current, 'fields.rateplanNameRenewal', '')} - Roaming`]: {
              ...deviceList
            }
          })
        };
      }
      return { ...acc };
    }, {});
  }, [rateplanList]);

  const onAddVehicleInfo = () => {
    if (
      !dataField.subscriber ||
      !dataField.plan ||
      !dataField.device ||
      !dataField.term ||
      !dataField.price ||
      !dataField.offer ||
      !(dataField.vin || (dataField.make && dataField.model && dataField.year))
    ) {
      setError('Please enter all necessary information');
      return;
    }
    setRenewalSubscriberList([...renewalSubscriberList, dataField]);
    setDataFields({
      subscriber: '',
      plan: '',
      device: '',
      term: '',
      price: '',
      offer: '',
      vin: '',
      make: '',
      model: '',
      year: ''
    });
    setError('');
  };

  const getPlanList = () =>
    rateplanModel &&
    Object.keys(rateplanModel).reduce(
      (acc, current) => [...acc, { text: current, value: current }],
      []
    );

  const getDevicesList = () =>
    (dataField.plan &&
      Object.keys(_.get(rateplanModel, dataField.plan, {})).reduce(
        (acc, current) => [...acc, { text: current, value: current }],
        []
      )) ||
    [];

  const getTermList = () =>
    (dataField.device &&
      Object.keys(
        _.get(rateplanModel, `${dataField.plan}.${dataField.device}`, {})
      ).reduce((acc, current) => {
        const is3year = current === value3year;
        if (is3year) return [...acc, { text: current, value: current }];
        return [...acc];
      }, [])) ||
    [];

  const getPrice = () => {
    const termPriceKey = dataField.plan.includes('- Roaming')
      ? 'termPrice50PrecentUs'
      : 'termPrice';
    return (
      dataField.term &&
      _.get(
        rateplanModel,
        `${dataField.plan}.${dataField.device}.${dataField.term}.${termPriceKey}`,
        ''
      )
    );
  };

  useEffect(() => {
    if (dataField.term) setDataFields({ ...dataField, price: getPrice() });
  }, [dataField.term]);

  useEffect(() => {
    setDataFields({ ...dataField, device: '', term: '', price: '' });
  }, [dataField.plan]);

  useEffect(() => {
    setDataFields({ ...dataField, term: '', price: '' });
  }, [dataField.device]);

  return (
    <Box between={3}>
      <Notification variant="branded" copy="en">
        <Strong>Note: </Strong>
        {vehicleInfoMessage}
      </Notification>
      <FlexGrid>
        <Box between={4}>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Input
                label="Subscriber"
                value={dataField.subscriber}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    subscriber: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <Select
                disabled={!dataField.subscriber}
                label="Plan"
                placeholder="Please select..."
                options={getPlanList()}
                value={dataField.plan}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    plan: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <Select
                disabled={!dataField.plan}
                label="Devices"
                placeholder="Please select..."
                options={getDevicesList()}
                value={dataField.device}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    device: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Select
                disabled={!dataField.device}
                label="Term"
                placeholder="Please select..."
                options={getTermList()}
                value={dataField.term}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    term: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
            {!isCorporate && (
              <FlexGrid.Col>
                <Input
                  disabled
                  label="Price"
                  value={dataField.price}
                  onChange={e =>
                    setDataFields({
                      ...dataField,
                      price: e.target.value
                    })
                  }
                />
              </FlexGrid.Col>
            )}
            <FlexGrid.Col>
              <Select
                disabled={!dataField.price}
                label="Offer"
                placeholder="Please select..."
                options={subscribersOfferList}
                value={dataField.offer}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    offer: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Input
                disabled={dataField.make || dataField.model || dataField.year}
                label={vehicleVIN}
                value={dataField.vin}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    vin: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <Input
                disabled={dataField.vin}
                label={vehicleMake}
                value={dataField.make}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    make: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <Input
                disabled={dataField.vin}
                label={vehicleModel}
                value={dataField.model}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    model: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row horizontalAlign="start">
            <FlexGrid.Col xs={4} md={4}>
              <Input
                disabled={dataField.vin}
                label={vehicleYear}
                pattern="[0-9]{4}"
                maxLength="4"
                value={dataField.year}
                onChange={e =>
                  setDataFields({
                    ...dataField,
                    year: e.target.value
                  })
                }
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </Box>
      </FlexGrid>
      <Box vertical={3} horizontal={3}>
        <Button onClick={onAddVehicleInfo}>Add</Button>
      </Box>
      {error && (
        <Notification
          variant="error"
          dismissible
          onExit={() => setError('')}
          copy="en"
        >
          {error}
        </Notification>
      )}
    </Box>
  );
};

AddSubscriberForm.propTypes = {
  isCorporate: PropTypes.bool.isRequired,
  rateplanList: PropTypes.objectOf(PropTypes.any).isRequired,
  renewalSubscriberList: PropTypes.arrayOf(PropTypes.object).isRequired,
  setRenewalSubscriberList: PropTypes.func.isRequired
};

export default AddSubscriberForm;
